import {
  calculateReceiptBreakdown,
  calculateReceiptPaymode,
  getCreateReceiptPayload,
  getDefaultReceiptService,
  validateCreateReceiptStore,
} from "@elixir/utils";
import { BillSummary, PaymodeSelector, PopupWrapper, WarningStrip } from "ui";
import { useReceiptStore } from "@elixir/stores";
import {
  closeReceiptPopupEvent,
  settleDues,
  settleDuesRaw,
  viewReceipt,
} from "../../utils/receipt";
import {
  CircleExclamationRegularIcon,
  IndianRupeeSignRegularIcon,
  PaperPlaneSolidIcon,
} from "@elixir/icons";
import {
  APPOINTMENT_CHANNEL,
  MODES_OF_APT,
  RECEIPT_FLOW_STATES,
  RECEIPT_PAYMENT_MODE_ID,
} from "@elixir/types";
import usePostCreateReceipt from "../../apis/tryst/create-receipt";
import usePutUpdateReceipt from "../../apis/tryst/update-receipt";
import ReceiptInputParams from "./input-fields/receipt-input-params";
import ServicesTable from "../receipt/services-table";
import AdditionalDiscount from "./input-fields/additional-discount";
import RemarksInput from "./input-fields/remarks";
import PaymentIdInputField from "./input-fields/payment-id";
import { useMyAccessProfileV2 } from "../../apis/tryst/get-my-access-profile";
import { notification } from "antd";
import { PROGRESS_STEPS } from "@elixir/stores/src/receipt-store";
import { docListMassaged } from "../receipt-customisation/customisation-utls";
import bookSlot from "../../controllers/appointments/book-slot";
import useGetQueueConfig from "../../apis/tryst/get-queue-config";
import { serializeErrorForTracking, track } from "../../utils/tracking";
import { blockTimerForNSeconds } from "../../payment-dashboard-v2/utils/generic-utils";
import { useEffect, useMemo, useState } from "react";
import {
  FirebaseAppointment,
  ServiceItemForAppointment,
} from "../../firestore/types";

function ReceiptForm() {
  const receiptState = useReceiptStore((state) => state.receiptState);
  const services = useReceiptStore((state) => state.services);
  const additionalDiscountConfig = useReceiptStore(
    (state) => state.additionalDiscountConfig
  );
  const paymodes = useReceiptStore((state) => state.paymodes);
  const setPaymodes = useReceiptStore((state) => state.setPaymodes);
  const closeReceiptPopup = useReceiptStore(
    (state) => state.setCloseReceiptPopup
  );

  const aid = useReceiptStore((state) => state.aid);
  const uhid = useReceiptStore((state) => state.uhid);
  const mobile = useReceiptStore((state) => state.phoneNumber);
  const ptName = useReceiptStore((state) => state.patientName);
  const clinicId = useReceiptStore((state) => state.clinicId);
  const patientId = useReceiptStore((state) => state.patientId);
  const doctor_oid = useReceiptStore((state) => state.doctor_oid);

  const { data: queueConfigData } = useGetQueueConfig();

  const setTypeOfReceipt = useReceiptStore((state) => state.setTypeOfReceipt);

  const {
    subTotal,
    totalDiscount,
    additionalDiscount,
    grandTotal,
    amountDue,
    amountPaid,
    totalDiscountOnEntireBill,
  } = calculateReceiptBreakdown({
    services,
    additionalDiscountConfig,
    paymodes,
  });

  const { valid, error } = validateCreateReceiptStore({
    services,
    additionalDiscountConfig,
    paymodes,
  });

  const { mutateAsync: createReceipt, isLoading: isCreatingReceipt } =
    usePostCreateReceipt();
  const { mutateAsync: updateReceipt, isLoading: isReceiptUpdating } =
    usePutUpdateReceipt();

  const createNewAppt = useReceiptStore((state) => state.createNewAppt);

  const selectedAppointment = useReceiptStore(
    (state) => state.selectedAppointment
  );

  const setSelectedAppointment = useReceiptStore(
    (state) => state.setSelectedAppointment
  );

  const allProfiles = docListMassaged();

  const activeDoctor = allProfiles?.doctors.find(
    (doc) => (selectedAppointment?.doctor_oid || doctor_oid) == doc.oid
  );

  const { data: myAccessProfile } = useMyAccessProfileV2();

  const onCreateOrEditReceipt = async () => {
    const receiptStore = useReceiptStore.getState();
    const { setCurrentStep, setIsProcessing } = useReceiptStore.getState();
    setIsProcessing(true);

    let finalAid = aid;

    const doctorIdForReceipt =
      activeDoctor?.oid ||
      myAccessProfile?.doctors.find(
        (i) => i.oid == selectedAppointment?.doctor_oid
      )?.oid ||
      doctor_oid;

    if (!finalAid) {
      try {
        const bookAppointment = await bookSlot({
          channel: APPOINTMENT_CHANNEL.WALKIN,
          clinicId: receiptStore?.clinicId!,
          doctorId: doctorIdForReceipt!,
          mode: MODES_OF_APT.IN_CLINIC,
          patient: {
            oid: receiptStore?.patientId as string,
            allowMultiBooking:
              queueConfigData?.data?.apt_config?.allow_pt_multi_apt,
          },
          endTime: new Date().toISOString(),
          startTime: new Date().toISOString(),
          action: "BOOK_AND_CHECK_IN",
          createNewAppt: !!createNewAppt,
        });
        if (bookAppointment.success) {
          finalAid = bookAppointment.data?.aid as string;
          await blockTimerForNSeconds(500);
        } else {
          track("ERROR_V2", {
            on: "FAILED_TO_BOOK_APPOINTMENT_NO_AID_CASE_2",
          });
          notification.error({
            message: "Failed to create appointment",
            placement: "bottomRight",
            duration: 3,
          });
          return;
        }
      } catch (e: any) {
        track("ERROR_V2", {
          on: "FAILED_TO_BOOK_APPOINTMENT_NO_AID_CASE",
          what: serializeErrorForTracking(e),
        });
      }
    }

    const createReceiptBody = getCreateReceiptPayload(
      {
        ...receiptStore,
        aid: finalAid,
      },
      receiptStore.receiptState === RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK
    );

    if (
      [
        RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK,
        RECEIPT_FLOW_STATES.CREATE_RECEIPT,
      ].includes(receiptState)
    ) {
      const { data, error } = await createReceipt({
        ...createReceiptBody,
        businessId: myAccessProfile?.business?.id!,
        doctor_oid: doctorIdForReceipt,
      });
      setTypeOfReceipt && setTypeOfReceipt(receiptState);
      if (error) {
        setIsProcessing(false);
        setCurrentStep(null);
        notification.error({
          message:
            typeof error === "object" && error !== null && "message" in error
              ? (error as any).message
              : typeof error === "string"
                ? error
                : "Failed to create receipt",
          placement: "bottomRight",
          duration: 3,
        });
        return;
      }
      if (data) {
        setIsProcessing(true);
        setCurrentStep(PROGRESS_STEPS.CREATING_RECEIPT);
        viewReceipt({ receipt: data });
        if (selectedAppointment) {
          setSelectedAppointment({
            ...selectedAppointment,
            receipts: [...selectedAppointment.receipts, data],
          });

          notification.success({
            message: "Receipt Created Successfully",
            placement: "bottomRight",
            duration: 3,
          });
        }
      }
    } else if (
      receiptState === RECEIPT_FLOW_STATES.EDIT_RECEIPT &&
      receiptStore.receiptId
    ) {
      const receiptData = createReceiptBody;
      const { data } = await updateReceipt({
        ...receiptData,
        meta: {
          ...receiptData.meta,
          doctor_oid: activeDoctor?.oid,
        },
        receiptId: receiptStore.receiptId,
        businessId: myAccessProfile?.business?.id!,
      });

      if (data) {
        setIsProcessing(true);
        setCurrentStep(PROGRESS_STEPS.CREATING_RECEIPT);
        viewReceipt({ receipt: data });
        if (selectedAppointment) {
          setSelectedAppointment({
            ...selectedAppointment,
            receipts: selectedAppointment.receipts.map((item) =>
              item.receipt_id === receiptStore.receiptId ? data : item
            ),
          });

          notification.success({
            message: "Receipt Updated Successfully",
            placement: "bottomRight",
            duration: 3,
          });
        }
      }
    }
  };

  const patientOwes = useReceiptStore((state) => state.patientOwes);
  const setServices = useReceiptStore((state) => state.setServices);

  const appointmentServices = (
    selectedAppointment as unknown as FirebaseAppointment
  )?.services as ServiceItemForAppointment[];

  const [consumedPillIndices, setConsumedPillIndices] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    setConsumedPillIndices(new Set());
  }, [receiptState]);

  const availablePills = useMemo(
    () =>
      (appointmentServices ?? []).filter((_, i) => !consumedPillIndices.has(i)),
    [appointmentServices, consumedPillIndices?.size]
  );

  const onPillClick = (
    pill: ServiceItemForAppointment,
    originalIndex: number
  ) => {
    const newService = getDefaultReceiptService({
      skuItem: {
        service_id: pill.service_id,
        service_name: pill.service_name,
        price: pill.price,
      },
    });
    const firstRowEmpty = services.length === 1 && !services[0].name;
    const updatedServices = firstRowEmpty
      ? [newService]
      : [...services, newService];
    setServices(updatedServices);

    const { grandTotal } = calculateReceiptBreakdown({
      services: updatedServices,
      additionalDiscountConfig,
      paymodes,
    });
    setPaymodes(calculateReceiptPaymode({ grandTotal, paymodes }));

    setConsumedPillIndices((prev) => new Set(prev).add(originalIndex));
    track("APPOINTMENT_SERVICE_PILL_CLICKED_IN_RECEIPT");
  };

  return (
    <PopupWrapper
      style={{ height: "75vh", overflowY: "scroll" }}
      title={
        receiptState === RECEIPT_FLOW_STATES.CREATE_RECEIPT
          ? "Create Receipt"
          : receiptState === RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK
            ? "Send Payment Link"
            : "Edit Receipt"
      }
      open={
        receiptState === RECEIPT_FLOW_STATES.EDIT_RECEIPT ||
        receiptState === RECEIPT_FLOW_STATES.CREATE_RECEIPT ||
        receiptState === RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK
      }
      onClose={() => {
        closeReceiptPopup();
        closeReceiptPopupEvent();
      }}
      onOk={onCreateOrEditReceipt}
      cancelText="Cancel"
      okText={
        receiptState === RECEIPT_FLOW_STATES.CREATE_RECEIPT
          ? "Create Receipt"
          : receiptState === RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK
            ? "Send Payment Link"
            : "Edit Receipt"
      }
      isOkButtonDisabled={!valid}
      isSubmitting={isCreatingReceipt || isReceiptUpdating}
      OkButtonIcon={PaperPlaneIcon}
    >
      <div className="dr-flex dr-flex-col dr-h-full dr-min-h-400 dr-relative">
        {error ? (
          <div className="dr-sticky dr-top-0 dr-z-50 dr-bg-darwin-neutral-0">
            <WarningStrip>
              <div className="dr-flex dr-items-center dr-space-x-2">
                <CircleExclamationRegularIcon className="dr-w-12 dr-h-12" />
                <p className="BodyRegular">{error}</p>
              </div>
            </WarningStrip>
          </div>
        ) : patientOwes ? (
          <div className="dr-sticky dr-top-0 dr-z-50 dr-bg-darwin-neutral-0">
            <WarningStrip
              cta="Clear Dues"
              onCtaAction={() => {
                if (selectedAppointment) {
                  settleDues({ appointment: selectedAppointment });
                } else if (clinicId && patientId) {
                  settleDuesRaw({
                    aid,
                    patient: {
                      mobile,
                      fln: ptName,
                      oid: patientId,
                      amount_pending: patientOwes,
                    },
                    uhid,
                    clinicId: clinicId,
                    doctor_oid,
                  });
                }
              }}
            >
              <div className="dr-flex dr-items-center dr-space-x-2">
                <IndianRupeeSignRegularIcon className="dr-w-12 dr-h-12" />
                <p className="BodyRegular">{`Previous Dues: Rs. ${patientOwes}`}</p>
              </div>
            </WarningStrip>
          </div>
        ) : null}
        {/* {onboarded_for?.includes("PASS") &&
        receiptState === RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK ? (
          <div className="dr-pt-16 dr-px-16">
            <div className="dr-bg-darwin-touch-yellow-bg-dark flex flex-row dr-rounded-8 dr-p-12 items-center">
              <CircleInfoRegularIcon className="dr-text-darwin-touch-yellow-primary" />
              <span className="BodyRegular ml-12">
                We will offer 30% discount only on
              </span>
              <span className="BodyBold">&nbsp;Consultation</span>
              <span className="BodyRegular">&nbsp;to Eka Gold pass users</span>
            </div>
          </div>
        ) : (
          <></>
        )} */}
        <ReceiptInputParams />
        {availablePills.length > 0 && (
          <div className="dr-flex dr-flex-wrap dr-gap-8 dr-px-16 dr-pb-12">
            {(appointmentServices ?? []).map((pill, originalIndex) =>
              consumedPillIndices.has(originalIndex) ? null : (
                <button
                  key={`${pill.service_id}-${originalIndex}`}
                  className="dr-flex dr-items-center dr-gap-4 dr-px-10 dr-py-4 dr-rounded-6 dr-border dr-border-darwin-primary-primary dr-bg-darwin-primary-bg dr-text-darwin-primary-primary dr-cursor-pointer dr-text-12 dr-font-500 dr-leading-16"
                  onClick={() => onPillClick(pill, originalIndex)}
                >
                  <span>{pill.service_name}</span>
                  <span>&#8377;{pill.price}</span>
                </button>
              )
            )}
          </div>
        )}
        <div className="dr-pb-16 dr-flex-1">
          <ServicesTable />
        </div>
        <div className="dr-flex dr-items-center dr-px-16 dr-py-12 dr-bg-darwin-neutral-50 dr-justify-between">
          <div className="dr-flex dr-flex-col dr-space-y-8">
            <AdditionalDiscount />
            <PaymodeSelector
              paymodes={paymodes}
              setPaymodes={setPaymodes}
              allowAddPaymode={
                receiptState !== RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK &&
                subTotal > 0
              }
              allowedPaymodes={
                RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK === receiptState
                  ? [
                      {
                        id: RECEIPT_PAYMENT_MODE_ID.EKA_POSTPAY,
                        isActive: true,
                        name: "Eka Payment",
                      },
                    ]
                  : undefined
              }
              amountDisabled={subTotal < 0}
            />
            {RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK === receiptState ? null : (
              <PaymentIdInputField />
            )}
          </div>
          <BillSummary
            totalAmount={subTotal}
            totalDiscount={totalDiscount}
            totalDiscountOnEntireBill={totalDiscountOnEntireBill}
            additionalDiscount={additionalDiscount}
            grandTotal={grandTotal}
            amountPaid={amountPaid}
            amountDue={amountDue}
          />
        </div>
        <div className="dr-px-16">
          <RemarksInput />
        </div>
      </div>
    </PopupWrapper>
  );
}

function PaperPlaneIcon() {
  return (
    <PaperPlaneSolidIcon className="dr-w-16 dr-h-16 dr-text-darwin-neutral-0" />
  );
}

export default ReceiptForm;
